import { OnInit, Component, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  PlantationCreateDto,
  PlantationUpdateDto,
} from '../proxy/geographic/dtos/plantation';
import { PLANTATION_CONFIG, PLANTATION_FIELDS } from './plantation.config';
import { BaseHierarchicalCrudComponent, CrudPage, GenericForm, IActionsSchema, ICrudService, normalizePropertyPrefilterQuery, resolveActions } from '@zendra/shared';
import { PlantationService } from '../proxy/geographic/controllers';
import { PlantationPlanningOrchestratorService } from 'orchestrator';
import {
  GetPlantationProjectionListInputDto,
  PlantationProjectionDto,
  PlantationProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/plantations/models';

@Component({
  selector: 'app-plantation',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, PlantationService, ConfirmationService],
  templateUrl: './plantation.html',
  styleUrl: './plantation.scss',
})
export class Plantation extends BaseHierarchicalCrudComponent<
  PlantationProjectionDto,
  PlantationCreateDto,
  PlantationUpdateDto,
  PlantationProjectionExcelDownloadDto
> implements OnInit {
  geographicService = inject(PlantationService);
  orchestratorService = inject(PlantationPlanningOrchestratorService);

  config = PLANTATION_CONFIG;
  fields = PLANTATION_FIELDS;
  actions: IActionsSchema | null = null;

  get showPrefilters(): boolean {
    return !this.isUsedInHierarchy;
  }

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = resolveActions(
      this.config.actions,
      {
        [this.config.actionIds.processPlantationTrigger]:
          () => this.processPlantation(),
        [this.config.actionIds.undoProcessPlantationTrigger]:
          () => this.undoProcessPlantation(),
      },
    );
  }

  service: ICrudService<PlantationProjectionDto, PlantationCreateDto, PlantationUpdateDto, PlantationProjectionExcelDownloadDto> = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.orchestratorService.update(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'plantationId',
    ),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetPlantationProjectionListInputDto) =>
      this.orchestratorService.getList(this.withRotationFilter(normalizePropertyPrefilterQuery(query))),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.orchestratorService.getListAsExcelFile(this.withRotationFilter(normalizePropertyPrefilterQuery(input))),
  };

  private withRotationFilter<T extends { rotationId?: number[] | null }>(query: T): T {
    return this.parentFilterField === 'rotationId' && this.parentId !== undefined
      ? { ...query, rotationId: [this.parentId] }
      : query;
  }

  protected prepareUpdateDto(data: any, selected: PlantationProjectionDto): PlantationUpdateDto {
    return {
      plantationDate: data.plantationDate,
      plantationArea: data.plantationArea,
      plantedSeedlings: data.plantedSeedlings,
      isPlantationCompleted: data.isPlantationCompleted,
      isOwnPlantation: data.isOwnPlantation,
      processedDate: data.processedDate,
      rotationId: data.rotationId,
      geneticMaterialId: data.geneticMaterialId,
      spacingId: data.spacingId,
      plantationTypeId: data.plantationTypeId,
      seedlingOriginId: data.seedlingOriginId,
      packingTypeId: data.packingTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'Plantation';
  }

  private processPlantation(): void {
    this.runBulkAction(ids =>
      this.orchestratorService.process({ ids })
    );
  }

  private undoProcessPlantation(): void {
    this.runBulkAction(ids =>
      this.orchestratorService.undoProcess({ ids })
    );
  }
}
